// The Voulgaris-Style Edge §6 (Edge 5) — CLV badge system.
//
// "CLV is the only honest measure of whether the process is working."
// Pure, no I/O. Derives the account-level CLV badge from the user's
// REAL per-bookmaker CLV already in user_betting_profile — never a
// fabricated metric.
//
// Doc §6.2 + §13 prohibitions, enforced here:
//   • CLV > 5%  ⇒ SHARP BETTOR
//   • CLV > 10% ⇒ PROFESSIONAL EDGE
//   • CLV ≤ 0   ⇒ NO badge (review methodology)
//   • Never display a CLV badge until ≥ 20 slips with closing odds
//     tracked — below that the sample is not meaningful.

import type { BookmakerEntry } from "../slips/useBettingProfile.ts";

/** Slip-count floor below which a CLV badge is never shown (doc §13). */
export const CLV_BADGE_MIN_SLIPS = 20;

export interface UserClvAggregate {
  /** Legs-weighted average CLV %, or null when no CLV is tracked. */
  readonly avgClvPct: number | null;
  /** Number of legs that actually carry a tracked CLV value. */
  readonly clvLegs: number;
}

/** Legs-weighted account CLV from the per-bookmaker profile slice.
 *  Entries without a clv_pct are excluded (honest — not counted as
 *  0). No tracked CLV anywhere ⇒ { null, 0 }. */
export function aggregateUserClv(
  bookmakerPerformance: Record<string, BookmakerEntry> | null | undefined,
): UserClvAggregate {
  if (!bookmakerPerformance) return { avgClvPct: null, clvLegs: 0 };
  let weighted = 0;
  let legs = 0;
  for (const e of Object.values(bookmakerPerformance)) {
    if (e == null || e.clv_pct == null || !Number.isFinite(e.clv_pct)) continue;
    const n = Number.isFinite(e.legs) && e.legs > 0 ? e.legs : 0;
    if (n === 0) continue;
    weighted += e.clv_pct * n;
    legs += n;
  }
  if (legs === 0) return { avgClvPct: null, clvLegs: 0 };
  return { avgClvPct: +(weighted / legs).toFixed(2), clvLegs: legs };
}

export type ClvBadgeTier = "sharp" | "professional";

export type ClvBadgeReason =
  | "insufficient_sample" // < 20 slips with closing odds
  | "no_clv_tracked" // no CLV data at all
  | "non_positive_clv" // CLV ≤ 0 — review methodology
  | "building"; // positive but below the SHARP threshold

export interface ClvBadge {
  readonly tier: ClvBadgeTier | null;
  readonly label: string | null;
  readonly avgClvPct: number | null;
  /** Why no badge (only set when tier is null) — honest, never hidden. */
  readonly reason: ClvBadgeReason | null;
}

export interface ClvBadgeInput {
  readonly avgClvPct: number | null;
  /** Slips with closing odds tracked (the doc's ≥20 gate). */
  readonly sampleSlips: number;
}

/** Resolve the CLV badge. Honest gates first; never a badge on a
 *  thin or non-positive sample. */
export function clvBadge({ avgClvPct, sampleSlips }: ClvBadgeInput): ClvBadge {
  if (avgClvPct === null || !Number.isFinite(avgClvPct)) {
    return { tier: null, label: null, avgClvPct: null, reason: "no_clv_tracked" };
  }
  if (sampleSlips < CLV_BADGE_MIN_SLIPS) {
    return {
      tier: null,
      label: null,
      avgClvPct,
      reason: "insufficient_sample",
    };
  }
  if (avgClvPct <= 0) {
    return { tier: null, label: null, avgClvPct, reason: "non_positive_clv" };
  }
  if (avgClvPct > 10) {
    return {
      tier: "professional",
      label: `PROFESSIONAL EDGE · CLV +${avgClvPct.toFixed(1)}%`,
      avgClvPct,
      reason: null,
    };
  }
  if (avgClvPct > 5) {
    return {
      tier: "sharp",
      label: `SHARP BETTOR · CLV +${avgClvPct.toFixed(1)}%`,
      avgClvPct,
      reason: null,
    };
  }
  return { tier: null, label: null, avgClvPct, reason: "building" };
}
